import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Switch, 
  Paper, 
  Divider, 
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  DragIndicator as DragIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { baseClient } from '../api/baseClient';
import { useNavigate, useParams } from 'react-router-dom';
import * as WorkflowTypes from '../types/Workflow';

// Type aliases for cleaner code
type WorkflowAction = WorkflowTypes.WorkflowAction;
type WorkflowCreate = WorkflowTypes.WorkflowCreate;
type ActionTypeConfig = WorkflowTypes.ActionTypeConfig;
type ActionParamField = WorkflowTypes.ActionParamField;

// Constants
const TRIGGER_TYPES = WorkflowTypes.TRIGGER_TYPES;
const ACTION_TYPES = WorkflowTypes.ACTION_TYPES;

// Initial form state
const initialFormState: WorkflowCreate = {
  name: '',
  description: '',
  trigger_type: '',
  trigger_conditions: {},
  actions: [],
  is_active: true
};

const WorkflowBuilder = () => {
  const [formData, setFormData] = useState<WorkflowCreate>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState('');
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Fetch workflow data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchWorkflow();
    }
  }, [id]);

  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      const response = await baseClient.get(`/workflows/${id}`);
      setFormData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching workflow:', err);
      setError('Failed to load workflow. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleTriggerTypeChange = (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => {
    const value = e.target.value as string;
    setFormData(prev => ({ 
      ...prev, 
      trigger_type: value,
      // Reset trigger conditions when trigger type changes
      trigger_conditions: {} 
    }));
  };

  const handleAddAction = () => {
    setSelectedActionType('');
    setEditingActionIndex(null);
    setActionDialogOpen(true);
  };

  const handleEditAction = (index: number) => {
    setSelectedActionType(formData.actions[index].type);
    setEditingActionIndex(index);
    setActionDialogOpen(true);
  };

  const handleDeleteAction = (index: number) => {
    const newActions = [...formData.actions];
    newActions.splice(index, 1);
    setFormData(prev => ({ ...prev, actions: newActions }));
  };

  const handleMoveAction = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === formData.actions.length - 1)
    ) {
      return;
    }

    const newActions = [...formData.actions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const [movedAction] = newActions.splice(index, 1);
    newActions.splice(newIndex, 0, movedAction);
    
    setFormData(prev => ({ ...prev, actions: newActions }));
  };

  const handleActionDialogClose = () => {
    setActionDialogOpen(false);
  };

  const handleActionTypeSelect = (type: string) => {
    setSelectedActionType(type);
  };

  const handleSaveAction = (actionData: WorkflowAction) => {
    let newActions = [...formData.actions];
    
    if (editingActionIndex !== null) {
      // Edit existing action
      newActions[editingActionIndex] = actionData;
    } else {
      // Add new action
      newActions.push(actionData);
    }
    
    setFormData(prev => ({ ...prev, actions: newActions }));
    setActionDialogOpen(false);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const newActions = [...formData.actions];
    const [movedAction] = newActions.splice(result.source.index, 1);
    newActions.splice(result.destination.index, 0, movedAction);
    
    setFormData(prev => ({ ...prev, actions: newActions }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.trigger_type) {
      errors.trigger_type = 'Trigger type is required';
    }
    
    if (formData.actions.length === 0) {
      errors.actions = 'At least one action is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      if (isEditMode) {
        // Update existing workflow
        await baseClient.put(`/workflows/${id}`, formData);
      } else {
        // Create new workflow
        await baseClient.post('/workflows', formData);
      }
      
      // Redirect back to workflows list
      navigate('/settings/workflows');
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError('Failed to save workflow. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings/workflows');
  };

  if (loading) {
    return <Typography>Loading workflow...</Typography>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography>{error}</Typography>
        </Paper>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Workflow Details</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Workflow Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleInputChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={handleInputChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.trigger_type}>
              <InputLabel id="trigger-type-label">Trigger</InputLabel>
              <Select
                labelId="trigger-type-label"
                name="trigger_type"
                value={formData.trigger_type}
                onChange={handleTriggerTypeChange}
                label="Trigger"
                required
              >
                {TRIGGER_TYPES.map(trigger => (
                  <MenuItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.trigger_type && (
                <FormHelperText>{validationErrors.trigger_type}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Actions</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddAction}
          >
            Add Action
          </Button>
        </Box>
        
        {validationErrors.actions && (
          <Typography color="error" sx={{ mb: 2 }}>{validationErrors.actions}</Typography>
        )}
        
        {formData.actions.length === 0 ? (
          <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
            No actions added yet. Click "Add Action" to create your workflow.
          </Typography>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="actions">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {formData.actions.map((action, index) => {
                    const actionConfig = ACTION_TYPES.find(type => type.type === action.type);
                    
                    return (
                      <Draggable key={index} draggableId={`action-${index}`} index={index}>
                        {(provided) => (
                          <Card 
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ mb: 2 }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center">
                                <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                                  <DragIcon color="action" />
                                </Box>
                                <Box flexGrow={1}>
                                  <Typography variant="subtitle1">
                                    {index + 1}. {actionConfig?.label || action.type}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {actionConfig?.description || 'Custom action'}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Box mt={1}>
                                {Object.entries(action.params).map(([key, value]) => (
                                  <Typography key={key} variant="body2">
                                    <strong>{key}:</strong> {typeof value === 'string' ? value : JSON.stringify(value)}
                                  </Typography>
                                ))}
                              </Box>
                            </CardContent>
                            <CardActions>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveAction(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveAction(index, 'down')}
                                disabled={index === formData.actions.length - 1}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                              <Box flexGrow={1} />
                              <Button size="small" onClick={() => handleEditAction(index)}>
                                Edit
                              </Button>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => handleDeleteAction(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </CardActions>
                          </Card>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>
      
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEditMode ? 'Update Workflow' : 'Create Workflow'}
        </Button>
      </Box>
      
      {/* Action Dialog */}
      <ActionDialog
        open={actionDialogOpen}
        onClose={handleActionDialogClose}
        selectedActionType={selectedActionType}
        onActionTypeSelect={handleActionTypeSelect}
        onSave={handleSaveAction}
        initialData={editingActionIndex !== null ? formData.actions[editingActionIndex] : undefined}
      />
    </Box>
  );
};

// Action Dialog Component
interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  selectedActionType: string;
  onActionTypeSelect: (type: string) => void;
  onSave: (actionData: WorkflowAction) => void;
  initialData?: WorkflowAction;
}

const ActionDialog = ({ 
  open, 
  onClose, 
  selectedActionType, 
  onActionTypeSelect, 
  onSave,
  initialData
}: ActionDialogProps) => {
  const [actionParams, setActionParams] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Reset form when dialog opens or action type changes
  useEffect(() => {
    if (open) {
      if (initialData && initialData.type === selectedActionType) {
        setActionParams(initialData.params || {});
      } else {
        setActionParams({});
      }
      setErrors({});
    }
  }, [open, selectedActionType, initialData]);
  
  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setActionParams(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setActionParams(prev => ({ ...prev, [name]: value }));
  };
  
  const validateActionParams = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!selectedActionType) {
      newErrors.actionType = 'Please select an action type';
      setErrors(newErrors);
      return false;
    }
    
    const actionConfig = ACTION_TYPES.find(type => type.type === selectedActionType);
    if (!actionConfig) return true;
    
    actionConfig.paramFields.forEach(field => {
      if (field.required && (!actionParams[field.name] || actionParams[field.name].trim() === '')) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = () => {
    if (!validateActionParams()) return;
    
    onSave({
      type: selectedActionType,
      params: actionParams
    });
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Edit Action' : 'Add Action'}
      </DialogTitle>
      <DialogContent>
        {!selectedActionType ? (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select an action type:
            </Typography>
            <Grid container spacing={2}>
              {ACTION_TYPES.map(actionType => (
                <Grid item xs={12} sm={6} md={4} key={actionType.type}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 }
                    }}
                    onClick={() => onActionTypeSelect(actionType.type)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {actionType.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {actionType.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {errors.actionType && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errors.actionType}
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Configure {ACTION_TYPES.find(type => type.type === selectedActionType)?.label}:
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {ACTION_TYPES.find(type => type.type === selectedActionType)?.paramFields.map(field => (
                <Grid item xs={12} key={field.name}>
                  {field.type === 'select' ? (
                    <FormControl fullWidth error={!!errors[field.name]}>
                      <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
                      <Select
                        labelId={`${field.name}-label`}
                        name={field.name}
                        value={actionParams[field.name] || ''}
                        onChange={handleSelectChange}
                        label={field.label}
                        required={field.required}
                      >
                        {field.options?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors[field.name] && (
                        <FormHelperText>{errors[field.name]}</FormHelperText>
                      )}
                      {field.helpText && !errors[field.name] && (
                        <FormHelperText>{field.helpText}</FormHelperText>
                      )}
                    </FormControl>
                  ) : field.type === 'textarea' ? (
                    <TextField
                      name={field.name}
                      label={field.label}
                      multiline
                      rows={4}
                      fullWidth
                      required={field.required}
                      value={actionParams[field.name] || ''}
                      onChange={handleParamChange}
                      placeholder={field.placeholder}
                      error={!!errors[field.name]}
                      helperText={errors[field.name] || field.helpText}
                    />
                  ) : (
                    <TextField
                      name={field.name}
                      label={field.label}
                      fullWidth
                      required={field.required}
                      value={actionParams[field.name] || ''}
                      onChange={handleParamChange}
                      placeholder={field.placeholder}
                      error={!!errors[field.name]}
                      helperText={errors[field.name] || field.helpText}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {selectedActionType && (
          <Button onClick={() => onActionTypeSelect('')} color="primary">
            Back to Action Types
          </Button>
        )}
        {selectedActionType && (
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Action
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowBuilder; 